<template>
    <v-container :fluid="true" class="max-height">
        <v-row>
            <v-col class="px-5 d-flex justify-space-between" cols="12">
                <h1 class="text-left">Legal Documents</h1>
                <v-btn color="black" prepend-icon="mdi-plus" @click="selectedDoc = null; dialogSwitch = true">Add</v-btn>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="12">
                <v-table>
                    <thead>
                    <tr>
                        <th>Title</th>
                        <th>Version</th>
                        <th>Doc Key</th>
                        <th>Portals</th>
                        <th>Effective Date</th>
                        <th>Expiry Date</th>
                        <th>Status</th>
                        <th>Actions</th>
                    </tr>
                    </thead>
                    <tbody>
                    <template v-if="legalDocs.length">
                    <tr v-for="doc in legalDocs" :key="doc.id" class="text-left">
                        <td>{{ doc.title }}</td>
                        <td>{{ doc.version }}</td>
                        <td>{{ doc.doc_key }}</td>
                        <td>
                            <v-btn color="primary" density="compact" icon="mdi-plus" size="small" text
                                   @click="managePortals(doc)"/>
                            <v-chip-group>
                                <template v-for="(portal,index) in doc.portals" :key="index">
                                    <v-chip size="x-small">
                                        <span class="me-1">{{ portal.name }}</span>
                                        <v-icon icon="mdi-close-circle" @click="portalsDetach(doc, portal)"/>
                                    </v-chip>
                                </template>
                            </v-chip-group>
                        </td>
                        <td>{{ doc.effective_date }}</td>
                        <td>{{ doc.expiry_date }}</td>
                        <td>
                            <v-chip :color="doc.status ? 'success' : 'grey'" density="compact" size="small">
                                {{ doc.status ? 'Active' : 'Inactive' }}
                            </v-chip>
                        </td>
                        <td>
                            <v-btn-group density="compact" divided variant="outlined">
                                <v-btn class="text-info" prepend-icon="mdi-file"
                                       size="x-small" @click="fetchDocUrl(doc.id)">Doc File
                                </v-btn>
                                <v-btn class="text-warning" prepend-icon="mdi-pencil"
                                       size="x-small" @click="editDoc(doc)">Edit
                                </v-btn>
                                <v-btn class="text-error" prepend-icon="mdi-delete" size="x-small"
                                       @click="deleteDoc(doc)">Delete
                                </v-btn>
                            </v-btn-group>
                        </td>
                    </tr>
                    </template>
                    <tr v-else>
                        <td v-if="loading" class="text-center" colspan="8">
                            <v-progress-circular color="primary" indeterminate/>
                        </td>
                        <td v-else class="text-center" colspan="8">No legal documents found.</td>
                    </tr>
                    </tbody>
                </v-table>
            </v-col>
        </v-row>

        <v-row v-if="totalPages > 1" justify="center">
            <v-col cols="8">
                <v-container class="max-width">
                    <v-pagination
                        v-model="currentPage"
                        :length="totalPages"
                        class="my-4"
                        density="compact"
                        @update:model-value="fetchLegalDocs"
                    ></v-pagination>
                </v-container>
            </v-col>
        </v-row>
    </v-container>

    <Create-Edit v-if="dialogSwitch" v-model="dialogSwitch" :doc="selectedDoc" @addDoc="addDoc" @updateDoc="updateDoc"/>
    <Doc-Portals v-if="portalsDialogSwitch" v-model="portalsDialogSwitch" :doc="selectedDoc" :portals="portals"
                 @updateDoc="updateDoc"/>
</template>

<script setup>
import {onMounted, ref} from "vue";
import {send} from "@/composables/apiClient";
import {useSnackbarStore} from "@/stores/snackBarStore";
import DocPortals from "@/components/dialogs/legal-documents/DocPortals.vue";
import CreateEdit from "@/components/dialogs/legal-documents/Create_Edit.vue";

const {showSnackbar} = useSnackbarStore();
const portalsDialogSwitch = ref(false);
const dialogSwitch = ref(false);
const selectedDoc = ref(null);
const currentPage = ref(1);
const totalPages = ref(1);
const loading = ref(true);
const legalDocs = ref([]);
const portals = ref([]);

onMounted(async () => {
    await fetchLegalDocs();
    fetchPortals();
});

const errors = ref([]);

const fetchLegalDocs = () => {
    send('get', `admin/legal-documents`, {page: currentPage.value})
        .then((response) => {
            legalDocs.value = response.data.data.data;
            totalPages.value = response.data.data.last_page;
            currentPage.value = response.data.data.current_page;
        })
        .catch((error) => {
            console.log(error)
            showSnackbar('Unable to load legal documents. Please try again.', 'error')
        }).finally(() => loading.value = false)
}
const addDoc = (doc) => {
    legalDocs.value.unshift(doc);
}
const editDoc = (doc) => {
    selectedDoc.value = doc;
    dialogSwitch.value = true;
}
const updateDoc = (doc) => {
    const index = legalDocs.value.findIndex(comp => comp.id === doc.id);
    if (index !== -1) {
        legalDocs.value.splice(index, 1, doc);
    }
}
const fetchDocUrl = (docId) => {
    send('get', `legal-document-url/${docId}`)
        .then((response) => {
            if (response.data.success && response.data.data.url) {
                window.open(response.data.data.url);
            }
        })
        .catch((error) => {
            console.log(error)
        })
}
const deleteDoc = (doc) => {
    if (confirm(`Are you sure to delete "${doc.title}" doc?`)) {
        send('delete', `admin/legal-documents/${doc.id}`)
            .then((response) => {
                if (response.data.success) {
                    legalDocs.value = legalDocs.value.filter(item => item.id !== doc.id);
                    showSnackbar(response.data.message);
                }
            })
            .catch((error) => {
                if (error.response?.data?.message) {
                    showSnackbar(error.response.data.message, 'error')
                } else {
                    showSnackbar(`"${doc.title}" Document could not be deleted`, 'error')
                }
            })
    }
}

/*Portals Management*/
const fetchPortals = () => {
    send('get', `admin/portals`)
        .then((response) => {
            portals.value = response.data.data;
        })
        .catch((error) => {
            console.log(error)
            showSnackbar('Unable to load portals. Please try again.', 'error')
        }).finally(() => loading.value = false)
}
const managePortals = (doc) => {
    selectedDoc.value = doc;
    portalsDialogSwitch.value = true;
}
const portalsDetach = (doc, portal) => {
    if (confirm(`Are you sure to remove "${doc.title}" from "${portal.name}" portal?`)) {
        const portals = {};
        doc.portals.filter(item => item.id !== portal.id).forEach(item => {
            portals[item.id] = {is_required: !!item.pivot?.is_required};
        });
        send('post', `admin/legal-documents/${doc.id}/portals`, {portals})
            .then((response) => {
                showSnackbar(response.data.message);
                if (response.data.success) {
                    updateDoc(response.data.data);
                }
            })
            .catch(function (errorResponse) {
                showSnackbar(errorResponse.response?.data?.message ?? 'Something went wrong', 'error');
            })
    }
}
</script>
